"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { isLibraryShareLandingPathname, parseBrowseState } from "@/lib/library/library-routes";
import { BrowseFrame } from "./browse-frame";
import type { CatalogShelf } from "@/lib/api/api-client";
import { LibrarySidebar } from "./library-sidebar";

/**
 * La coque de `/library*` : la carte du répertoire à gauche, la page à droite.
 *
 * Montée par `app/(main)/library/layout.tsx`, donc la sidebar survit au
 * passage d'une page de bibliothèque à l'autre — son défilement et sa position
 * ne sautent pas quand on ouvre une fiche.
 *
 * # Le tiroir et son seul bouton
 *
 * Sous 768 px la sidebar devient un tiroir (voir `browse-frame.tsx` pour le
 * choix du seuil). Le studio pilote le sien depuis sa toolbar ; la
 * bibliothèque n'a pas de toolbar, donc le bouton vit ici, en tête du canevas
 * et seulement là où il sert à quelque chose.
 *
 * # Le seul défilement de la vue est ici
 *
 * Un unique conteneur `overflow-y-auto` pour toutes les pages : les fils
 * d'Ariane s'y collent en `sticky`, et il n'y a qu'un endroit où le
 * comportement de défilement peut mal tourner.
 *
 * # L'atterrissage d'un lien de partage n'a pas de carte
 *
 * `/library/share/<token>` se rend sans sidebar : le visiteur n'a pas de
 * bibliothèque à parcourir, et lui en montrer une avant qu'il ait vu le morceau
 * serait lui demander de choisir alors qu'on lui a envoyé quelque chose de
 * précis. La décision se prend ici plutôt que par une coque à part : Next ne
 * sait pas *retirer* un layout parent, seulement en ajouter — un groupe de
 * routes n'y aurait rien changé.
 *
 * Les `children` restent ceux que le layout serveur a rendus : cette coque les
 * transporte, elle ne les re-rend pas. La page d'atterrissage reste donc un
 * composant serveur, avec ses métadonnées d'aperçu.
 *
 * # Les fournisseurs sont au-dessus, dans le layout
 *
 * Les comptes, les playlists et le retour d'action appartiennent à la
 * bibliothèque et non à son habillage : ils sont montés par
 * `app/(main)/library/layout.tsx`, au-dessus de la frontière `Suspense`. Montés
 * ici, ils auraient manqué aux pages pendant le repli de cette frontière, qui
 * rend les enfants seuls.
 */

export function LibraryShell({
  children,
  selections,
}: {
  children: React.ReactNode;
  /** Lues par le layout, pour que la sidebar sorte pleine du serveur. */
  selections: readonly CatalogShelf[];
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const state = useMemo(() => parseBrowseState(searchParams), [searchParams]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  /*
    La mise en page ne passe plus par `useBreakpoint()` — voir `browse-frame`,
    le CSS s'en charge. Le palier reste lu ici pour ce que le CSS ne peut pas
    faire : refermer le tiroir quand la vue s'élargit, et écouter Échap. Un
    battement d'une frame y est sans conséquence, rien n'est peint dessus.
  */
  const compact = useBreakpoint() === "compact";

  /*
    Le tiroir se referme quand la vue s'élargit. Sans ça, en revenant à une
    largeur étroite il se rouvrirait tout seul, par-dessus une page que
    personne n'a demandé de recouvrir. Ajusté pendant le rendu plutôt que dans
    un effet — React relance avant de peindre, aucune frame intermédiaire.
  */
  if (!compact && drawerOpen) {
    setDrawerOpen(false);
  }

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Échap referme le tiroir : c'est un calque, il doit se fermer au clavier
  // comme il se ferme au doigt sur le voile.
  useEffect(() => {
    if (!compact || !drawerOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [compact, drawerOpen]);

  /*
    Décidé sur le chemin et non sur une prop : la coque est montée par le
    layout, qui ne sait pas quelle page il sert.
  */
  const bare = isLibraryShareLandingPathname(pathname);

  if (bare) {
    return <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>;
  }

  return (
    <BrowseFrame
      sidebar={<LibrarySidebar selections={selections} state={state} pathname={pathname} onNavigate={closeDrawer} />}
      drawerOpen={drawerOpen}
      onCloseSidebar={closeDrawer}
    >
      {/*
        `md:hidden` et non `{compact && …}` : le bouton d'ouverture du tiroir
        suit le même seuil CSS que la sidebar, sinon il apparaîtrait sur desktop
        le temps que le palier se résolve.
      */}
      <div className="shrink-0 px-4 py-2 border-b border-border-subtle md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-expanded={drawerOpen}
          aria-controls="library-sidebar"
          className="inline-flex items-center gap-2 h-8 px-3 rounded-lg text-[12px] text-text-secondary hover:text-text-primary hover:bg-white/[0.06] transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <RailIcon />
          Les sélections
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
    </BrowseFrame>
  );
}

/** Trois traits : « une liste de sélections », pas « un menu ». */
function RailIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M2 3.5h10M2 7h10M2 10.5h6" />
    </svg>
  );
}
